import React, { useState, useEffect, useContext } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ApplicationRoles } from '../../helpers';

import {
    AllProjectsPage,
    MyProjectsPage,
    DashboardPage,    
    RolesPage,
    AssignmentListPage,
    ReportsPage,
    BudgetPage,
    LogsPage,
    SapInitiativesPage,
    DimensionPage,
    HomePage,
    GlobalSettingsPage,
    ManageProjectManagersPage
} from '../pages';

import {UserContext} from '../contexts'

// Looking for an icon name?
// look here! https://material-ui.com/components/material-icons/
import PeopleAltIcon from '@material-ui/icons/PeopleAltOutlined';
import BulletList from "@material-ui/icons/FormatListBulleted";
import CalendarTodayOutlined from '@material-ui/icons/CalendarTodayOutlined';
import InsertDriveFile from '@material-ui/icons/InsertDriveFile';
import History from '@material-ui/icons/History';
import HomeIcon from '@material-ui/icons/Home';
import CreateIcon from '@material-ui/icons/Create';
import SettingsIcon from '@material-ui/icons/Settings';

export const NavRoutes = {
    Home: "/",
    AllProjects: "/allprojects",
    MyProjectsPage: "/myprojects",
    Logout: "/logout",
    NotFound: "/notfound",
    NoSettings:"/nosettings",
    Forbidden: "/forbidden",   
    ManageProjectManagers: "/manageprojectmanagers/all", 
    EditProjectManagers: "/manageprojectmanagers/edit/:teamId",
    Admin: {
        Dashboard: "/admin",
        EditBudgetYears: "/admin/budget/all",
        Roles: "/admin/roles/all",
        AssignmentList: "/admin/assignmentlist/all",
        EditOwnerAssignment: "/admin/assignmentlist/edit/:teamId",
        Reports: "/admin/reports/all",
        EditReport: "/admin/reports/edit/:reportId",
        Logs: "/admin/logs/all",
        ViewLog: "/admin/logs/view/:projectId",
        EditRole: "/admin/roles/edit/:roleId",
        AddUserInRole: "/admin/roles/edit/:roleId/adduser",
        EditUserInRole: "/admin/roles/edit/:roleId/edituser/:userKey",
        //UploadSapInitiatives: "/admin/sapinitiatives/all",
        Dimensions: "/admin/dimension/all",
		GlobalSettings: "/admin/globalsettings/all"
    }
};

export const NavContext = React.createContext(null);

const { Provider } = NavContext;
export const NavProvider = ({ ...props }) => {
    const history = useHistory();
    const location = useLocation();
    const {draftRequests} = useContext(UserContext)
    
    const [breadcrumbs, setBreadcrumbs] = useState(JSON.parse(sessionStorage.getItem("breadcrumbs") || "[]"));

    const navigateTo = (route, args) => {
        route = compileRoute(route, args);
        history.push(route);
    }

    const switchTo = (route, args) => {
        route = compileRoute(route, args);
        history.replace(route);
    }

    const getRouteTo = (route, args) => {
        route = compileRoute(route, args);
        return route;
    }

    const compileRoute = (route, args) => {
        if(route.includes(":") && args && args.length > 0) {
            for(let i = 0; i < args.length; i++) {
                let startIndex = route.indexOf(":", 0);
                if(startIndex === -1) break;
                let endIndex = route.indexOf("/", startIndex);
                if(endIndex === -1) endIndex = route.length - 1;
                else endIndex--;
                const token = route.substr(startIndex, (endIndex - startIndex) + 1);
                route = route.replace(token, args[i]);               
            }          
        }

        return route;        
    }

    const setBreadcrumbRoot = (title, path) => {        
        const tokens = [{ title, url: path ? path : location.pathname }];    
        setBreadcrumbs(tokens);
        sessionStorage.setItem("breadcrumbs", JSON.stringify(tokens));
    }

    const setBreadcrumbToken = (title) => {
        const tokens = [].concat(breadcrumbs);
        const token = tokens.find(x => x.url === location.pathname);
		if(token) {
			token.title = title;
			setBreadcrumbs(tokens);
			sessionStorage.setItem("breadcrumbs", JSON.stringify(tokens));
		}
    }

    const setBreadcrumbTokens = (tokens) => {
        if(tokens){
            setBreadcrumbs(tokens)
            sessionStorage.setItem("breadcrumbs", JSON.stringify(tokens));
        }
    }

    const addBreadcrumbToken = (title) => {        
        let tokens = [].concat(breadcrumbs);
        if(tokens.find(x => x.url === location.pathname)) {
            const index = tokens.findIndex(x => x.url === location.pathname);
            tokens = tokens.slice(0, index + 1);
        } else {
            tokens.push({ title, url: location.pathname });
        }

        setBreadcrumbs(tokens);
        sessionStorage.setItem("breadcrumbs", JSON.stringify(tokens));
    }

    const clearBreadcrumbs = () => {
        setBreadcrumbs([]);
    }    

    const getCurrentLocation = () => {
        return location;
    }

    const [menuItems, setMenuItems] = useState([]);
    useEffect(() => {

        // Add here all menu items
        setMenuItems([            
            // Map here left menu items
            {
                title: "Home",
                routePath: NavRoutes.Home,
                icon: <HomeIcon color="primary" />,
                isProtected: true,
                component: HomePage,
                isAdmin: false                
            },
            {
                title: "All Projects",
                routePath: NavRoutes.AllProjects,
                icon: <BulletList color="primary" />,
                isProtected: true,
                component: AllProjectsPage,
                isAdmin: false                
            },
            {
                title: "My Projects",
                routePath: NavRoutes.MyProjectsPage,
                icon: <BulletList color="primary" />,
                isProtected: true,
                component: MyProjectsPage,
                isAdmin: false
            },

            // Map here admin menu items
            // First item is used to route the "go to admin dashboard" link
            // Other items will be rendered in the block menu
            {
                routePath: NavRoutes.Admin.Dashboard,
                isProtected: true,
                component: DashboardPage,
                isAdmin: true                
            },
            {
                title: "Roles",
                description: "View and manage user role assignations. Here you can monitor and handle user authorizations.",
                routePath: NavRoutes.Admin.Roles,
                icon: <PeopleAltIcon />,
                isProtected: true,
                component: RolesPage,
                isAdmin: true
            },
            {
                title: "Project Managers assignment",
                description: "View and handle assignments between owners and project managers.",
                routePath: NavRoutes.Admin.AssignmentList,
                icon: <PeopleAltIcon />,
                isProtected: true,
                component: AssignmentListPage,
                isAdmin: true
            },  
            {
                title: "Reports",
                description: "Here you can monitor and add PowerBI reports",
                routePath: NavRoutes.Admin.Reports,
                icon:
                    <svg style={{width: '1em', height: '1em', minWidth: '56px', color: '#fff', marginLeft: window.document.documentMode ? '-7px' : '0px'}} className="MuiSvgIcon-root MuiSvgIcon-colorPrimary" focusable="false" viewBox="0 0 24 24" aria-hidden="true" role="presentation">
                        <path d="M20.38 8.57l-1.23 1.85a8 8 0 01-.22 7.58H5.07A8 8 0 0115.58 6.85l1.85-1.23A10 10 0 003.35 19a2 2 0 001.72 1h13.85a2 2 0 001.74-1 10 10 0 00-.27-10.44z" />
                        <path d="M10.59 15.41a2 2 0 002.83 0l5.66-8.49-8.49 5.66a2 2 0 000 2.83z" />
                    </svg>,
                isProtected: true,
                component: ReportsPage,
                isAdmin: true                
            },
            {
                title: "Years and budget sessions management",
                description: "Here you can monitor budget session and add new years.",
                routePath: NavRoutes.Admin.EditBudgetYears,
                icon: <CalendarTodayOutlined />,
                isProtected: true,
                component: BudgetPage,
                isAdmin: true                
            },
            {
                title: "Logs",
                description: "Here you can monitor all projects hystory.",
                routePath: NavRoutes.Admin.Logs,
                icon: <History />,
                isProtected: true,
                component: LogsPage,
                isAdmin: true                
            },
            /*{
                title: "Upload SAP initiatives",
                description: "Here you can upload excel files to import actual values.",
                routePath: NavRoutes.Admin.UploadSapInitiatives,
                icon: <InsertDriveFile />,
                isProtected: true,
                component: SapInitiativesPage,
                isAdmin: true                
            },*/
            {
                title: "Dimensions",
                description: "Here you can manage the dimensions.",
                routePath: NavRoutes.Admin.Dimensions,
                icon: <CreateIcon />,
                isProtected: true,
                component: DimensionPage,
                isAdmin: true
            },
            {
                title: "Global settings",
                description: "Here you can manage the global settings.",
                routePath: NavRoutes.Admin.GlobalSettings,
                icon: <SettingsIcon />,
                isProtected: true,
                component: GlobalSettingsPage,
                isAdmin: true                
            },
            {
                title: "Manage project managers",
                description: "Here you can manage project managers.",
                routePath: NavRoutes.ManageProjectManagers,
                icon: <PeopleAltIcon color="primary"/>,
                isProtected: true,
                component: ManageProjectManagersPage,
                isAdmin: false,
                roles:[ApplicationRoles.Owner]                
            },
        ]);

    }, [draftRequests])

    return (
        <Provider value={{
            menuItems,
            navigateTo,            
            switchTo,
            getRouteTo,
            breadcrumbs,
            setBreadcrumbRoot,
            setBreadcrumbToken,
            setBreadcrumbTokens,
            addBreadcrumbToken,
            clearBreadcrumbs,
            getCurrentLocation,
        }}>
            {props.children}
        </Provider>
    );
}